import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Ban,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  RotateCcw,
  Send,
  TriangleAlert,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useAdminQuestionnaireTemplates,
  useQuestionnaireConfig,
  useQuestionnaireDeliveryMethods,
} from '@/features/admin-questionnaire-templates/api';
import { useQuestionnaireMutations } from '@/features/patient-record/hooks/useQuestionnaireMutations';
import { patientQueries } from '@/features/patient-record/queries';
import { questionnaireStatusToBadgeVariant } from '@/lib/clinicalBadges';
import { formatDate } from '@/lib/format';
import type {
  QuestionnaireDelivery,
  QuestionnaireRevealLink,
  QuestionnaireViewModel,
} from '@/features/patient-record/types';

import { ClinicalSection, type ClinicalSectionLabels } from './ClinicalSection';
import { ConfirmDeleteDialog } from './_shared/ConfirmDeleteDialog';
import { QuestionnaireAttachmentsList } from './_shared/QuestionnaireAttachmentsList';
import {
  DeliveryStatusBadge,
  QuestionnaireDeliveryHistory,
} from './_shared/QuestionnaireDeliveryStatus';
import { RevealLinkModal } from './_shared/RevealLinkModal';

// NOT_SENT (URL/manual) can be marked-as-sent or revoked; SENT/OPENED can be revoked.
const REVOKABLE_STATUSES = new Set(['NOT_SENT', 'SENT', 'OPENED']);

// A URL request can have its link rotated while it is still active.
function canRegenerate(q: QuestionnaireViewModel): boolean {
  return q.deliveryMethod === 'URL' && REVOKABLE_STATUSES.has(q.status);
}

interface QuestionnairesSectionProps {
  readonly guid: string;
}

export function QuestionnairesSection({ guid }: QuestionnairesSectionProps): ReactElement {
  const { t } = useTranslation();
  const query = useQuery(patientQueries.questionnaires(guid));
  const [sendOpen, setSendOpen] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<QuestionnaireViewModel | null>(null);
  const [pendingRegen, setPendingRegen] = useState<QuestionnaireViewModel | null>(null);
  const [reveal, setReveal] = useState<QuestionnaireRevealLink | null>(null);
  const { revoke, markSent, regenerate } = useQuestionnaireMutations(guid);
  const configQuery = useQuestionnaireConfig();
  // Optimistic while loading; only block once the backend confirms PPQ is unconfigured.
  const notConfigured = configQuery.isSuccess && !configQuery.data.configured;

  const labels: ClinicalSectionLabels = {
    title: t('patient.questionnaires.heading'),
    empty: t('patient.questionnaires.empty'),
    emptyIcon: <ClipboardCheck className="h-5 w-5" aria-hidden="true" />,
    loading: t('patient.questionnaires.loading'),
    errorHeading: t('patient.questionnaires.error.heading'),
    errorMessage: t('patient.questionnaires.error.message'),
    codeLabel: t('routes.patient.error.codeLabel'),
    retryLabel: t('routes.patient.error.retry'),
  };

  return (
    <>
      <ClinicalSection
        id="questionnaires"
        labels={labels}
        query={query}
        summary={(query.data ?? []).map((q) => {
          const status = t(`patient.questionnaires.status.${q.status}`);
          const dateNote =
            q.completedAt !== null
              ? t('patient.questionnaires.completedAt', { value: formatDate(q.completedAt) })
              : q.sentAt !== null
                ? t('patient.questionnaires.sentAt', { value: formatDate(q.sentAt) })
                : null;
          return {
            label: q.templateName,
            value: dateNote !== null ? `${status} · ${dateNote}` : status,
            filled: true,
          };
        })}
        itemKey={(q) => q.id}
        renderItem={(q) => (
          <QuestionnaireRow
            guid={guid}
            questionnaire={q}
            onMarkSent={
              q.deliveryMethod === 'URL' && q.status === 'NOT_SENT'
                ? () => {
                    markSent.mutate(q.id);
                  }
                : null
            }
            onRegenerate={
              canRegenerate(q)
                ? () => {
                    setPendingRegen(q);
                  }
                : null
            }
            onRevoke={
              REVOKABLE_STATUSES.has(q.status)
                ? () => {
                    setPendingRevoke(q);
                  }
                : null
            }
          />
        )}
        headerAction={
          notConfigured ? (
            <div className="flex items-center gap-1.5">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-amber-500"
                      aria-label={t('patient.questionnaires.notConfigured')}
                    >
                      <TriangleAlert className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    {t('patient.questionnaires.notConfigured')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button size="sm" variant="outline" disabled>
                <Send className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
                {t('patient.questionnaires.send')}
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSendOpen(true);
              }}
            >
              <Send className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
              {t('patient.questionnaires.send')}
            </Button>
          )
        }
      />
      {sendOpen && (
        <SendQuestionnaireDialog
          guid={guid}
          onReveal={setReveal}
          onClose={() => {
            setSendOpen(false);
          }}
        />
      )}
      {pendingRevoke !== null && (
        <ConfirmDeleteDialog
          title={t('patient.questionnaires.revokeConfirmTitle')}
          message={t('patient.questionnaires.revokeConfirmMessage', {
            name: pendingRevoke.templateName,
          })}
          confirmLabel={t('patient.questionnaires.revoke')}
          busy={revoke.isPending}
          onCancel={() => {
            setPendingRevoke(null);
          }}
          onConfirm={() => {
            revoke.mutate(pendingRevoke.id, {
              onSettled: () => {
                setPendingRevoke(null);
              },
            });
          }}
        />
      )}
      {pendingRegen !== null && (
        <ConfirmDeleteDialog
          title={t('patient.questionnaires.regenerateConfirmTitle')}
          message={t('patient.questionnaires.regenerateConfirmMessage', {
            name: pendingRegen.templateName,
          })}
          confirmLabel={t('patient.questionnaires.regenerate')}
          busy={regenerate.isPending}
          onCancel={() => {
            setPendingRegen(null);
          }}
          onConfirm={() => {
            regenerate.mutate(pendingRegen.id, {
              onSuccess: (link) => {
                setReveal(link);
              },
              onSettled: () => {
                setPendingRegen(null);
              },
            });
          }}
        />
      )}
      {reveal !== null && (
        <RevealLinkModal
          link={reveal}
          onClose={() => {
            setReveal(null);
          }}
        />
      )}
    </>
  );
}

interface QuestionnaireRowProps {
  readonly guid: string;
  readonly questionnaire: QuestionnaireViewModel;
  readonly onMarkSent: (() => void) | null;
  readonly onRegenerate: (() => void) | null;
  readonly onRevoke: (() => void) | null;
}

function QuestionnaireRow({
  guid,
  questionnaire,
  onMarkSent,
  onRegenerate,
  onRevoke,
}: QuestionnaireRowProps): ReactElement {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isEmail = questionnaire.deliveryMethod === 'EMAIL';
  // EMAIL questionnaires carry delivery_record attempts; load them to show the latest push status
  // on the row and the full attempt history in the disclosure. URL/SMS rows skip this.
  const deliveriesQuery = useQuery({
    ...patientQueries.questionnaireDeliveries(guid, questionnaire.id),
    enabled: isEmail,
  });
  const deliveries = deliveriesQuery.data ?? [];
  const latestDelivery = deliveries[0] ?? null;
  const isExpired = questionnaire.status === 'EXPIRED';
  const expiresAt = questionnaire.expiresAt;
  const showExpiryHint =
    expiresAt !== null &&
    questionnaire.completedAt === null &&
    questionnaire.status !== 'REVOKED' &&
    !isExpired;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="group flex items-start gap-2">
        <button
          type="button"
          aria-expanded={expanded}
          aria-label={t('patient.questionnaires.attachments.toggle')}
          onClick={() => {
            setExpanded((open) => !open);
          }}
          className="flex flex-1 min-w-0 cursor-pointer items-start gap-2 rounded text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="mt-0.5 shrink-0 text-muted-foreground">
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            )}
          </span>
          <span className="flex-1 min-w-0 flex flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-card-foreground flex-1 min-w-0 truncate">
              {questionnaire.templateName}
            </span>
            <Badge
              variant={questionnaireStatusToBadgeVariant(questionnaire.status)}
              className="h-5 px-2 rounded-full text-[10px] font-semibold uppercase tracking-wide shrink-0"
            >
              {t(`patient.questionnaires.status.${questionnaire.status}`)}
            </Badge>
            {latestDelivery !== null && <DeliveryStatusBadge status={latestDelivery.status} />}
          </span>
          <span className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            {questionnaire.deliveryMethod !== null && (
              <span>{t(`patient.questionnaires.delivery.${questionnaire.deliveryMethod}`)}</span>
            )}
            {questionnaire.sentAt !== null && (
              <span>
                {t('patient.questionnaires.sentAt', { value: formatDate(questionnaire.sentAt) })}
              </span>
            )}
            {questionnaire.completedAt !== null && (
              <span>
                {t('patient.questionnaires.completedAt', {
                  value: formatDate(questionnaire.completedAt),
                })}
              </span>
            )}
            {showExpiryHint && (
              <span>{t('patient.questionnaires.expiresAt', { value: formatDate(expiresAt) })}</span>
            )}
            {isExpired && expiresAt !== null && (
              <span>{t('patient.questionnaires.expired', { value: formatDate(expiresAt) })}</span>
            )}
            {questionnaire.jobExecutionId !== null && (
              <span>{t('patient.questionnaires.partOfJob', { jobId: questionnaire.jobExecutionId })}</span>
            )}
          </span>
          </span>
        </button>
        <div className="flex shrink-0 items-center gap-0.5 opacity-70 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          {onMarkSent !== null && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onMarkSent}
              className="h-7 gap-1 px-2 text-[11px] text-muted-foreground hover:text-foreground"
            >
              <Send className="h-3.5 w-3.5" aria-hidden="true" />
              {t('patient.questionnaires.markSent')}
            </Button>
          )}
          {onRegenerate !== null && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onRegenerate}
              aria-label={t('patient.questionnaires.regenerate')}
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          )}
          {onRevoke !== null && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onRevoke}
              aria-label={t('patient.questionnaires.revoke')}
              className="h-7 w-7 p-0"
            >
              <Ban className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="ml-5 flex flex-col gap-3 border-l border-border pl-3">
          {questionnaire.jobExecutionId !== null && (
            <Link
              to={`/browse/questionnaires?tab=bulk-send&job=${String(questionnaire.jobExecutionId)}`}
              className="text-xs text-primary hover:underline"
            >
              {t('patient.questionnaires.viewJob', { jobId: questionnaire.jobExecutionId })}
            </Link>
          )}
          {isEmail && (
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t('patient.questionnaires.deliveries.heading')}
              </span>
              {deliveriesQuery.isPending ? (
                <p className="text-xs text-muted-foreground">
                  {t('patient.questionnaires.deliveries.loading')}
                </p>
              ) : deliveriesQuery.isError ? (
                <p className="text-xs text-destructive">
                  {t('patient.questionnaires.deliveries.error')}
                </p>
              ) : (
                <QuestionnaireDeliveryHistory deliveries={deliveries} />
              )}
            </div>
          )}
          <QuestionnaireAttachmentsList guid={guid} requestGuid={questionnaire.id} />
        </div>
      )}
    </div>
  );
}

interface SendQuestionnaireDialogProps {
  readonly guid: string;
  readonly onReveal: (link: QuestionnaireRevealLink) => void;
  readonly onClose: () => void;
}

function SendQuestionnaireDialog({
  guid,
  onReveal,
  onClose,
}: SendQuestionnaireDialogProps): ReactElement {
  const { t } = useTranslation();
  const { send } = useQuestionnaireMutations(guid);
  const templatesQuery = useAdminQuestionnaireTemplates();
  const deliveryQuery = useQuestionnaireDeliveryMethods();
  const patientQuery = useQuery(patientQueries.detail(guid));

  const templates = useMemo(
    () => (templatesQuery.data ?? []).filter((tmpl) => tmpl.active),
    [templatesQuery.data],
  );
  // PTV2-1255: SMS delivery is hidden for now. Filter it out of the picker so
  // only the supported methods (Email / link) are offered.
  const deliveryMethods = useMemo(
    () => (deliveryQuery.data ?? []).filter((d) => d.id !== 'SMS'),
    [deliveryQuery.data],
  );

  const [templateId, setTemplateId] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState<QuestionnaireDelivery>('EMAIL');
  // URL delivery mints a one-time link, so it requires an explicit confirmation step first.
  const [confirming, setConfirming] = useState(false);

  // Default the picker to the first active template once the catalog loads.
  useEffect(() => {
    if (templateId.length === 0 && templates.length > 0) {
      setTemplateId(templates[0]?.id ?? '');
    }
  }, [templates, templateId]);

  const isUrl = deliveryMethod === 'URL';

  // PTV2-1254: Email / SMS delivery need a matching contact point on file —
  // a link (URL) mints a one-time URL and needs none. When the required
  // contact is missing we block the send up-front with a clear, actionable
  // message instead of letting the request fail with a generic error.
  const contactPoints = patientQuery.data?.demographics.contactPoints ?? [];
  const hasEmail = contactPoints.some(
    (c) => c.type === 'EMAIL' && c.value.trim().length > 0,
  );
  const hasPhone = contactPoints.some(
    (c) => (c.type === 'PHONE' || c.type === 'SMS') && c.value.trim().length > 0,
  );
  const requiredContactMissing =
    deliveryMethod === 'EMAIL' && !hasEmail
      ? 'EMAIL'
      : deliveryMethod === 'SMS' && !hasPhone
        ? 'SMS'
        : null;
  // Only enforce once the patient record has loaded so we never block on
  // a not-yet-fetched contact list.
  const contactMissing = patientQuery.isSuccess && requiredContactMissing !== null;

  const canSubmit = templateId.length > 0 && !send.isPending && !contactMissing;

  const doSend = (): void => {
    if (templateId.length === 0) return;
    const template = templates.find((tmpl) => tmpl.id === templateId);
    send.mutate(
      {
        templateId,
        templateName: template?.label ?? templateId,
        deliveryMethod,
      },
      {
        onSuccess: (created) => {
          if (created.reveal !== null) {
            onReveal(created.reveal);
          }
          onClose();
        },
      },
    );
  };

  const onFormSubmit = (): void => {
    if (!canSubmit) return;
    // URL delivery → confirm first; EMAIL/SMS → dispatch immediately (no reveal).
    if (isUrl) {
      setConfirming(true);
    } else {
      doSend();
    }
  };

  if (confirming) {
    return (
      <ConfirmDeleteDialog
        title={t('patient.questionnaires.createConfirmTitle')}
        message={t('patient.questionnaires.createConfirmMessage')}
        confirmLabel={t('patient.questionnaires.createConfirm')}
        confirmVariant="default"
        busy={send.isPending}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={doSend}
      />
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-md" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('patient.questionnaires.send')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('patient.questionnaires.send')}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            onFormSubmit();
          }}
        >
          <label className="block space-y-1 text-xs font-medium">
            {t('patient.questionnaires.templateLabel')}
            <Select
              value={templateId}
              onChange={(event) => {
                setTemplateId(event.target.value);
              }}
              className="h-9 text-sm"
              disabled={templates.length === 0}
            >
              {templates.length === 0 && (
                <option value="">{t('patient.questionnaires.noTemplates')}</option>
              )}
              {templates.map((tmpl) => (
                <option key={tmpl.id} value={tmpl.id}>
                  {tmpl.label}
                </option>
              ))}
            </Select>
          </label>
          <label className="block space-y-1 text-xs font-medium">
            {t('patient.questionnaires.deliveryLabel')}
            <Select
              value={deliveryMethod}
              onChange={(event) => {
                setDeliveryMethod(event.target.value as QuestionnaireDelivery);
              }}
              className="h-9 text-sm"
            >
              {deliveryMethods.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.label}
                </option>
              ))}
            </Select>
          </label>
          {contactMissing && (
            <p role="alert" className="text-xs text-destructive">
              {t(
                `patient.questionnaires.missingContact.${requiredContactMissing}`,
              )}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={send.isPending}>
              {t('ui.dialog.close')}
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              loading={send.isPending}
              loadingText={t('patient.dirtyFooter.saving')}
            >
              {isUrl ? t('patient.questionnaires.create') : t('patient.questionnaires.send')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
